import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { exportDispatchManifest, exportFiles, uploadFiles } from '../api/uploadedFiles';
import { UploadedCsvFile, UploadedFile } from '../@types/uploaded-files.type';
import { getUploadedCsvFile, getUploadedFilePresignedUrlById, getUploadedFiles, updateUploadedFileData, getUploadedFilePresignedUrlByKey } from '../api/uploadedFiles';
import { ExportFilesKey, UploadedFilesKey, UploadedImagesKey, UploadMultipleImagesKey } from '../configs/uploaded-files';
import { ConfigurationHistoriesKey } from '../configs/part-type';

export const useExportFile = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exportInfo: any) => {
      const response = await exportFiles(exportInfo);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ExportFilesKey]
      });
    }
  });
};

export const useGetUploadedFiles = (entityId: number, entityType: string, notIncludeUrlsByDefault?: boolean): UseQueryResult<[UploadedFile]> => {
  return useQuery({
    queryKey: [UploadedFilesKey, entityId, entityType],
    queryFn: () => entityId && getUploadedFiles(entityId, entityType, notIncludeUrlsByDefault)
  });
};

export const useGetUploadedImages = (entityId: number, entityType: string): UseQueryResult<[UploadedFile]> => {
  return useQuery({
    queryKey: [UploadMultipleImagesKey, entityId, entityType],
    queryFn: () => entityId !== -1 && getUploadedFiles(entityId, entityType),
    enabled: false
  });
};

export const useUpdateUploadedFileAditionalData = (entityId: number): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      await updateUploadedFileData(data.id, data.isMain);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [UploadedImagesKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, entityId]
      });
    }
  });
};

export const useGetUploadedCsvFile = (key: string): UseQueryResult<UploadedCsvFile> => {
  return useQuery({
    queryKey: [UploadedFilesKey],
    queryFn: () => getUploadedCsvFile(key),
    enabled: false
  });
};

export const useGetUploadedFilePresignedUrlById = (id: number, contentDispositionType?: string): UseQueryResult<UploadedCsvFile> => {
  return useQuery({
    queryKey: [UploadedFilesKey, id, contentDispositionType],
    queryFn: () => getUploadedFilePresignedUrlById(id, contentDispositionType),
    enabled: false,
    refetchOnWindowFocus: false
  });
};

export const useGetUploadedFilePresignedUrlByKey = (key: string, mimeType?: string, contentDispositionType?: string): UseQueryResult<UploadedCsvFile> => {
  return useQuery({
    queryKey: [UploadedFilesKey, key, mimeType, contentDispositionType],
    queryFn: () => getUploadedFilePresignedUrlByKey(key, mimeType, contentDispositionType),
    enabled: false,
    refetchOnWindowFocus: false
  });
};

export const useExportDispatchManifest = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exportInfo: any) => {
      const response = await exportDispatchManifest(exportInfo);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ExportFilesKey]
      });
    }
  });
};

export const useUploadFiles = (entityId: number, entityType: string): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      await uploadFiles(data.entityId, data.formData, data.entityType);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, entityId]
      });
      await queryClient.invalidateQueries({
        queryKey: [UploadMultipleImagesKey, entityId, entityType]
      });
    }
  });
};